import { DEFAULT_TABLE_NAVIGATION_SCOPE } from '@experiences/components/table/blocs/table-keyboard-scopes';
import {
  getFocusedColumnId,
  useTableFocusStore,
} from '@experiences/components/table/blocs/stores/table-focus-store';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { useCallback, useEffect, useId, useTransition } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useRafThrottle } from '@experiences/hooks/use-raf-throttle';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';

interface UseTableKeyboardNavigationOptions {
  /**
   * Ordered list of editable column IDs for Tab/Shift+Tab navigation.
   */
  editableColumns?: string[];
  /**
   * Whether the table is active for keyboard interactions.
   */
  isActive?: boolean;
  /**
   * Keyboard shortcut scope for arrow key navigation.
   */
  keyboardShortcutScope?: string;
  onFocusedIndexChange?: (index: number | null) => void;
  /**
   * Total number of rows in the table.
   */
  rowCount: number;
  /**
   * Current sorting state - used to reset focus when sorting changes.
   */
  sorting?: unknown;
}

interface TableKeyboardNavigationResult {
  /** Clears the focused row index for this table. */
  clearFocus: () => void;
  /** Currently focused column ID, or null. */
  focusedColumn: string | null;
  /** Currently focused row index, or null. */
  focusedIndex: number | null;
  /** Unique table identifier. */
  tableId: string;
}

/**
 * Hook that manages keyboard navigation for the shared table components.
 * Handles focus state, arrow key navigation, and cleanup.
 *
 * Extracts common logic shared between table.tsx (native) and table.web.tsx.
 *
 * Performance optimization: When isActive=false, this hook skips most operations
 * to avoid unnecessary store updates and subscriptions for hidden tables.
 */
export const useTableKeyboardNavigation = ({
  editableColumns,
  isActive,
  keyboardShortcutScope = DEFAULT_TABLE_NAVIGATION_SCOPE,
  onFocusedIndexChange,
  rowCount,
  sorting,
}: UseTableKeyboardNavigationOptions): TableKeyboardNavigationResult => {
  const tableId = useId();
  const [, startTransition] = useTransition();

  // PERF: Use getState() for action functions instead of selectors.
  // These functions are stable and don't need reactivity - using selectors
  // creates unnecessary subscriptions that fire on every store update.
  const getStoreActions = useCallback(() => {
    const store = useTableFocusStore.getState();
    return {
      moveFocusDown: store.moveFocusDown,
      moveFocusUp: store.moveFocusUp,
      removeTable: store.removeTable,
      setActiveTableId: store.setActiveTableId,
      setEditableColumns: store.setEditableColumns,
      setFocusedIndex: store.setFocusedIndex,
      setTotalRows: store.setTotalRows,
    };
  }, []);

  // Clear focus when sorting changes so stale indices don't point at the wrong row.
  useEffect(() => {
    if (!isActive) {
      return;
    }
    const { setFocusedIndex } = getStoreActions();
    setFocusedIndex(tableId, null);
    // Intentionally excluding getStoreActions, tableId, and isActive (see comment above)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  // Keep total rows and editable columns in sync (only for active table)
  // PERF: Skip for inactive tables
  useEffect(() => {
    if (!isActive) {
      return;
    }
    const { setEditableColumns, setTotalRows } = getStoreActions();
    setTotalRows(tableId, rowCount);
    if (editableColumns) {
      setEditableColumns(tableId, editableColumns);
    }
  }, [rowCount, editableColumns, getStoreActions, tableId, isActive]);

  // Set active table when isActive changes.
  // When pendingFocusRestore is set (e.g. after build/review mode toggle),
  // restore focus to the last focused row (or row 0 if none) so the
  // quantity input receives focus.
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const { setActiveTableId } = getStoreActions();
    setActiveTableId(tableId);

    const {
      lastFocusedIndexByTableId,
      pendingFocusRestore,
      totalRowsByTableId,
    } = useTableFocusStore.getState();
    if (pendingFocusRestore) {
      const totalRows = totalRowsByTableId[tableId] ?? 0;
      if (totalRows > 0) {
        const lastFocused = lastFocusedIndexByTableId[tableId] ?? 0;
        const indexToFocus = Math.min(lastFocused, totalRows - 1);
        const { setFocusedIndex } = getStoreActions();
        setFocusedIndex(tableId, indexToFocus);
      }
      useTableFocusStore.setState({ pendingFocusRestore: false });
    }

    return () => {
      const { activeTableId } = useTableFocusStore.getState();
      if (activeTableId === tableId) {
        const { setActiveTableId: setActive } = getStoreActions();
        setActive(null);
      }
    };
  }, [isActive, getStoreActions, tableId]);

  // Clean up table from store when screen loses focus
  useFocusEffect(
    useCallback(() => {
      return () => {
        const { removeTable } = getStoreActions();
        removeTable(tableId);
      };
    }, [getStoreActions, tableId]),
  );

  // Throttle arrow key events to once per animation frame
  // Prevents event queue from filling up when holding down arrow keys
  const throttle = useRafThrottle();

  // Arrow key handlers - throttled to once per animation frame.
  // PERF: startTransition defers store update and re-renders so key response is immediate (better INP).
  const handleMoveFocusUp = useCallback(() => {
    throttle(() => {
      startTransition(() => {
        const { moveFocusUp } = getStoreActions();
        const newIndex = moveFocusUp(tableId);
        onFocusedIndexChange?.(newIndex);
      });
    });
  }, [
    throttle,
    startTransition,
    getStoreActions,
    tableId,
    onFocusedIndexChange,
  ]);

  const handleMoveFocusDown = useCallback(() => {
    throttle(() => {
      startTransition(() => {
        const { moveFocusDown } = getStoreActions();
        const newIndex = moveFocusDown(tableId);
        onFocusedIndexChange?.(newIndex);
      });
    });
  }, [
    throttle,
    startTransition,
    getStoreActions,
    tableId,
    onFocusedIndexChange,
  ]);

  // Register keyboard shortcuts
  useScreenScopedKeyboardShortcuts(
    Key.ArrowUp,
    handleMoveFocusUp,
    [handleMoveFocusUp],
    {},
    !!isActive,
    keyboardShortcutScope,
  );

  useScreenScopedKeyboardShortcuts(
    Key.ArrowDown,
    handleMoveFocusDown,
    [handleMoveFocusDown],
    {},
    !!isActive,
    keyboardShortcutScope,
  );

  // Reactive subscriptions for focus state — consumed by table components
  // for auto-scroll behavior without importing the store directly.
  const focusedIndex = useTableFocusStore(
    (state) => state.focusedIndexByTableId[tableId] ?? null,
  );

  const focusedColumn = useTableFocusStore((state) =>
    getFocusedColumnId(state.columnsByTableId[tableId]),
  );

  const clearFocus = useCallback(() => {
    const store = useTableFocusStore.getState();
    store.setFocusedIndex(tableId, null);
    store.setFocusedColumn(tableId, null);
  }, [tableId]);

  return { clearFocus, focusedColumn, focusedIndex, tableId };
};
